<template>
  <v-card outlined class="promotion-card flex-grow-1">
    <div class="promotion-card__header d-flex justify-space-between">
      <div class="text-h5 font-weight-bold d-flex align-center">
        <span>{{ $t('entities.promotions.entity') }}</span>
        <v-tooltip v-if="canShowDeleteButton && canEditDeletePromotion" bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              depressed
              x-small
              dark
              fab
              color="error"
              :disabled="isEditing || isDeletingPromotion"
              class="ml-4"
              v-bind="attrs"
              v-on="on"
              @click.stop="openDeleteDialog">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>
            {{ $t('entities.promotions.card.tooltips.deletePromotion') }}
          </span>
        </v-tooltip>
      </div>
      <div
        v-if="canEditDeletePromotion"
        class="promotion-card__header-actions d-flex align-center">
        <div class="promotion-card__icon-actions ml-auto d-flex align-center">
          <v-tooltip v-if="!isEditing" bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                x-small
                dark
                fab
                color="primary"
                class="mr-1"
                v-bind="attrs"
                v-on="on"
                @click.stop="handleEdit">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>
              {{ $t('entities.promotions.card.tooltips.editPromotion') }}
            </span>
          </v-tooltip>
          <v-tooltip v-if="isEditing" bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                x-small
                dark
                fab
                color="error"
                :disabled="isSaving"
                class="mr-1"
                v-bind="attrs"
                v-on="on"
                @click.stop="handleCancel">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>{{
              $t('entities.promotions.card.tooltips.cancelEditing')
            }}</span>
          </v-tooltip>
          <v-tooltip v-if="isEditing" bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                x-small
                dark
                fab
                color="primary"
                class="mr-1"
                :loading="isSaving"
                :disabled="isSaving"
                v-bind="attrs"
                v-on="on"
                @click.stop="handleSave">
                <v-icon small>mdi-check</v-icon>
              </v-btn>
            </template>
            <span>
              {{ $t('entities.promotions.card.tooltips.savePromotion') }}
            </span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <v-row dense class="mt-2">
      <v-col cols="12" md="3">
        <div class="promotion-card__image-row d-flex align-stretch">
          <label
            class="promotion-card__image-wrapper mb-3 mb-md-0"
            :class="{
              'promotion-card__image-wrapper--editable': isEditing,
              'promotion-card__image-wrapper--has-image':
                imagePreview || currentPromotion.image,
            }">
            <input
              v-if="isEditing"
              class="promotion-card__image-input"
              ref="imageInput"
              type="file"
              accept="image/*"
              :disabled="isUploadingImage || isSaving"
              @change="handleImageChange" />
            <template v-if="imagePreview || currentPromotion.image">
              <v-img
                :src="imagePreview || currentPromotion.image"
                max-height="180"
                contain />
            </template>
            <template v-else>
              <div class="promotion-card__image-placeholder">
                {{ $t('entities.promotions.card.imagePlaceholder') }}
              </div>
            </template>
            <div
              v-if="isEditing"
              class="promotion-card__image-overlay d-flex align-center justify-center">
              <v-progress-circular
                v-if="isUploadingImage"
                indeterminate
                size="18"
                width="2"
                color="primary"
                class="mr-2" />
              <span>{{ $t('entities.promotions.card.changeImage') }}</span>
            </div>
          </label>
        </div>
      </v-col>
      <v-col cols="12" md="5">
        <PromotionFields
          :local-promotion="localPromotion"
          :errors="errors"
          :is-editing="isEditing"
          :arena-currency="arenaCurrency"
          @update-name="updatePromotionName"
          @update-description="updatePromotionDescription"
          @discount-input="handleDiscountInput"
          @discount-blur="handleDiscountBlur"
          @update-active="updatePromotionActive"
          @discount-type-change="handleDiscountTypeChange" />
      </v-col>
      <v-col cols="12" md="4">
        <PriceSummary
          :price-summary="priceSummary"
          :arena-currency="arenaCurrency"
          :is-calculating="isCalculatingVariantPrices" />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <div
          class="promotion-card__components-header d-flex justify-space-between align-center flex-wrap">
          <div class="text-h6 font-weight-bold">
            {{ $t('entities.promotions.card.componentsTitle') }}
          </div>
          <v-btn
            v-if="canEditDeletePromotion"
            color="primary"
            depressed
            small
            class="text-none"
            :disabled="!isEditing"
            @click="openGroupDialog()">
            {{ $t('entities.promotions.card.addComponent') }}
          </v-btn>
        </div>

        <ComponentsTable
          :promotion="currentPromotion"
          :categories="categories"
          :is-editing="isEditing"
          :is-group-deleting="isGroupDeleting"
          @edit-group="openGroupDialog"
          @delete-group="handleGroupDelete" />
      </v-col>
    </v-row>

    <div v-if="shouldShowErrors" class="promotion-card__errors mt-4">
      <v-alert type="error" dense outlined>
        <div v-if="serverError" class="mb-1">{{ serverError }}</div>
        <div v-for="(message, key) in errors" :key="key">{{ message }}</div>
      </v-alert>
    </div>
    <GroupUpsertDialog
      :categories="categories"
      :group="selectedGroupForUpsert"
      :is-opened="isGroupDialogVisible"
      :arena-currency="arenaCurrency"
      @dialog:close="isGroupDialogVisible = false"
      @promotion-group:upsert="handleGroupSave" />
    <PromotionDeleteDialog
      v-if="canShowDeleteButton && canEditDeletePromotion"
      v-model="isDeleteDialogVisible"
      :promotion="promotion"
      :error="deleteError"
      :loading="isDeletingPromotion"
      @cancel="closeDeleteDialog"
      @confirm="confirmDelete" />
  </v-card>
</template>

<script>
import FilesApi from '@/services/misc/FilesApi'
import ArenaProductsApi from '@/services/shop/ArenaProductsApi'
import PromotionsApi from '@/services/shop/PromotionsApi'
import { mapActions } from 'vuex'
import GroupUpsertDialog from '../group-upsert-dialog/index.vue'
import PromotionDeleteDialog from '../DeleteDialog.vue'
import PriceSummary from './PriceSummary.vue'
import PromotionFields from './PromotionFields.vue'
import ComponentsTable from './ComponentsTable.vue'
import isRole from '@/mixins/crud/computed/isRole'
import {
  calculatePromotionPrice,
  createLocalPromotionState,
  createPriceSummaryState,
  extractCategoryCodeFromVariants,
  formatDiscountInputValue,
  getCategoryCodeById,
  getCategoryIdByCode,
  isFixedPricePromotion,
  normalizeDiscountAmount,
  normalizeVariantId,
  normalizeVariantIdList,
  roundCurrencyValue,
  sanitizeDiscountInput,
} from '@/utils/promotions'

export default {
  name: 'PromotionUpsertCard',
  mixins: [isRole],
  components: {
    GroupUpsertDialog,
    PromotionDeleteDialog,
    PriceSummary,
    PromotionFields,
    ComponentsTable,
  },
  props: {
    promotion: { type: Object, required: true },
    categories: { type: Array, default: () => [] },
    arenaCurrency: { type: Object, default: () => ({}) },
    isEditing: { type: Boolean, default: false },
    isNew: { type: Boolean, default: false },
    isSaving: { type: Boolean, default: false },
    serverError: { type: String, default: '' },
  },
  data() {
    return {
      localPromotion: this.createLocalPromotion(this.promotion),
      errors: {},
      imagePreview: '',
      imageFile: null,
      isUploadingImage: false,
      isGroupDialogVisible: false,
      priceSummary: {
        ...createPriceSummaryState(),
      },
      groupDeletionStates: {},
      variantDetailsCache: {},
      variantProductIndex: {},
      hasLoadedVariantProductIndex: false,
      variantIndexPromise: null,
      priceCalculationHandle: null,
      priceCalculationId: 0,
      isCalculatingVariantPrices: false,
      selectedGroupForUpsert: {},
      isDeleteDialogVisible: false,
      isDeletingPromotion: false,
      deleteError: '',
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena?.id
    },
    currentPromotion() {
      return this.isEditing ? this.localPromotion : this.promotion
    },
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0
    },
    shouldShowErrors() {
      return this.hasValidationErrors || Boolean(this.serverError)
    },
    canShowDeleteButton() {
      return Boolean(this.promotion?.id)
    },
    canEditDeletePromotion() {
      return this.isAdmin || this.isManager
    },
  },
  watch: {
    promotion: {
      deep: true,
      handler() {
        if (!this.isEditing) {
          this.localPromotion = this.createLocalPromotion(this.promotion)
          this.resetImageSelection()
        }
        this.resetGroupDeletionStates()
        this.schedulePriceCalculation()
        this.resetDeleteState()
      },
    },
    isEditing(value) {
      if (value) {
        this.localPromotion = this.createLocalPromotion(this.promotion)
        this.resetImageSelection()
      } else {
        this.resetValidation()
        this.resetImageSelection()
        this.isGroupDialogVisible = false
      }
      this.resetGroupDeletionStates()
      this.schedulePriceCalculation()
      if (value) {
        this.resetDeleteState()
      }
    },
    'localPromotion.name'(value) {
      if (value && this.errors.name) {
        this.$delete(this.errors, 'name')
      }
    },
    'localPromotion.discount_value'(value) {
      if (this.errors.discount_value && this.isValidDiscountString(value)) {
        this.$delete(this.errors, 'discount_value')
      }
      this.schedulePriceCalculation()
    },
    'localPromotion.discount_type'() {
      this.schedulePriceCalculation()
    },
    'localPromotion.groups': {
      deep: true,
      handler() {
        if (this.isEditing) {
          this.schedulePriceCalculation()
        }
      },
    },
    'promotion.groups': {
      deep: true,
      handler() {
        if (!this.isEditing) {
          this.schedulePriceCalculation()
        }
      },
    },
    arenaId(newArenaId, oldArenaId) {
      if (newArenaId === oldArenaId) return
      this.resetVariantPricingCache()
      this.schedulePriceCalculation()
    },
  },
  mounted() {
    this.schedulePriceCalculation()
  },
  beforeDestroy() {
    this.resetImageSelection()
    if (this.priceCalculationHandle) {
      clearTimeout(this.priceCalculationHandle)
      this.priceCalculationHandle = null
    }
  },
  methods: {
    ...mapActions(['showSnackbar']),
    createLocalPromotion(promotion) {
      return createLocalPromotionState(promotion, this.categories)
    },
    handleDiscountInput(value) {
      if (!this.localPromotion) return
      const sanitized = sanitizeDiscountInput(value)
      if (sanitized === this.localPromotion.discount_value) return
      this.$set(this.localPromotion, 'discount_value', sanitized)
    },
    handleDiscountBlur() {
      this.applyDiscountFormatting()
    },
    handleDiscountTypeChange(newDiscountType) {
      this.$set(this.localPromotion, 'discount_type', newDiscountType)
      this.$delete(this.errors, 'discount_value')

      let sp = ''
      if (newDiscountType === 'fixed_price') {
        const standardPriceNum = Number(this.localPromotion.standard_price)
        if (standardPriceNum > 0) {
          sp = standardPriceNum.toFixed(2)
        }
        this.$set(this.localPromotion, 'discount_value', sp)
      } else {
        this.$set(this.localPromotion, 'discount_value', '')
      }
    },
    applyDiscountFormatting() {
      if (!this.localPromotion) return ''
      const formatted = formatDiscountInputValue(
        this.localPromotion.discount_value,
      )
      this.$set(this.localPromotion, 'discount_value', formatted)
      return formatted
    },
    getGroupKey(group) {
      if (!group) return ''
      return group.id || group.tempId || ''
    },
    generateTempGroupId() {
      return `temp-group-${Date.now()}-${Math.random().toString(16).slice(2)}`
    },
    prepareVariantIdsForStorage(
      selectedVariantIds = [],
      persistedVariantIds = [],
      currentPendingVariantIds = [],
      shouldFilterExisting = false,
    ) {
      if (!shouldFilterExisting) {
        return [...selectedVariantIds]
      }
      const selectionSet = new Set(selectedVariantIds)
      const persistedSet = new Set(persistedVariantIds)
      const pendingSet = new Set()
      currentPendingVariantIds.forEach((variantId) => {
        if (selectionSet.has(variantId)) {
          pendingSet.add(variantId)
        }
      })
      selectedVariantIds.forEach((variantId) => {
        if (pendingSet.has(variantId) || persistedSet.has(variantId)) return
        pendingSet.add(variantId)
      })
      return Array.from(pendingSet)
    },
    schedulePriceCalculation() {
      if (this.priceCalculationHandle) {
        clearTimeout(this.priceCalculationHandle)
      }
      this.priceCalculationHandle = setTimeout(() => {
        this.priceCalculationHandle = null
        this.calculatePriceSummary()
      }, 150)
    },
    async calculatePriceSummary() {
      const groups = this.currentPromotion.groups
      const entries = this.collectVariantEntries(groups)
      const variantIds = Array.from(
        new Set(entries.map((entry) => entry.variantId).filter(Boolean)),
      )
      const calculationId = ++this.priceCalculationId
      if (!variantIds.length || !this.arenaId) {
        this.applyPriceSummary(calculationId, 0)
        return
      }
      this.isCalculatingVariantPrices = true
      try {
        await Promise.all(
          variantIds.map(async (variantId) => {
            await this.getVariantGrossPrice(variantId, entries)
          }),
        )
        const availableVariants = variantIds
          .map((variantId) => this.variantDetailsCache[variantId]?.variant)
          .filter(Boolean)
        const total = calculatePromotionPrice(
          this.currentPromotion,
          availableVariants,
          this.arenaCurrency,
        )
        if (calculationId !== this.priceCalculationId) return
        this.applyPriceSummary(calculationId, total)
      } catch (error) {
        console.error('Failed to calculate price summary', error)
      } finally {
        if (calculationId === this.priceCalculationId) {
          this.isCalculatingVariantPrices = false
        }
      }
    },
    collectVariantEntries(groups = []) {
      const entries = []
      groups.forEach((group, groupIndex) => {
        const quantity = Math.max(0, Number(group?.quantity) || 0)
        const variantMap = new Map()
        const registerVariant = (variantRef, shouldIndexProduct = false) => {
          const variantId = normalizeVariantId(variantRef)
          if (!variantId) return
          const productId = this.extractVariantProductId(variantRef)
          if (!variantMap.has(variantId)) {
            variantMap.set(variantId, {
              groupIndex,
              variantId,
              productId,
              quantity,
            })
          } else if (productId && !variantMap.get(variantId).productId) {
            variantMap.get(variantId).productId = productId
          }
          if (
            shouldIndexProduct &&
            productId &&
            (!this.variantProductIndex[variantId] ||
              !this.variantProductIndex[variantId].productId)
          ) {
            this.variantProductIndex[variantId] = { productId }
          }
        }
        const variantIds = Array.isArray(group?.variantIds)
          ? group.variantIds
          : []
        variantIds.forEach((variantRef) => registerVariant(variantRef, false))
        const variants = Array.isArray(group?.variants) ? group.variants : []
        variants.forEach((variant) => registerVariant(variant, true))
        entries.push(...variantMap.values())
      })
      return entries
    },
    extractVariantProductId(value) {
      if (value === null || value === undefined || typeof value !== 'object') {
        return ''
      }
      return (
        value?.arena_product_id ||
        value?.product_id ||
        value?.arena_product?.id ||
        ''
      )
    },
    async getVariantGrossPrice(variantId, entries = []) {
      if (!variantId || !this.arenaId) return 0
      const cached = this.variantDetailsCache[variantId]
      if (cached && cached.grossPrice !== undefined) {
        return cached.grossPrice
      }
      const productId =
        this.findProductIdInEntries(variantId, entries) ||
        cached?.productId ||
        (await this.lookupVariantProductId(variantId))
      if (!productId) return 0
      try {
        const response = await ArenaProductsApi.getArenaVariant(
          this.arenaId,
          productId,
          variantId,
        )
        const variantData = response.data || {}
        const grossPrice = this.extractVariantGrossPrice(variantData)
        this.variantDetailsCache[variantId] = {
          productId,
          grossPrice,
          variant: variantData,
        }
        if (!this.variantProductIndex[variantId]) {
          this.variantProductIndex[variantId] = { productId }
        } else if (!this.variantProductIndex[variantId].productId) {
          this.variantProductIndex[variantId].productId = productId
        }
        return grossPrice
      } catch (error) {
        console.error('Failed to fetch variant data', error)
        return 0
      }
    },
    findProductIdInEntries(variantId, entries = []) {
      const entry = entries.find(
        (candidate) => candidate.variantId === variantId && candidate.productId,
      )
      return entry?.productId || ''
    },
    async lookupVariantProductId(variantId) {
      if (this.variantProductIndex[variantId]?.productId) {
        return this.variantProductIndex[variantId].productId
      }
      await this.ensureVariantProductIndex()
      return this.variantProductIndex[variantId]?.productId || ''
    },
    async ensureVariantProductIndex() {
      if (this.hasLoadedVariantProductIndex || this.variantIndexPromise) {
        if (this.variantIndexPromise) {
          await this.variantIndexPromise
        }
        return
      }
      const arenaId = this.arenaId
      if (!arenaId) return
      this.variantIndexPromise = (async () => {
        try {
          let page = 1
          let lastPage = 1
          const perPage = 100
          do {
            const response = await ArenaProductsApi.list(arenaId, {
              page,
              size: perPage,
            })
            const responseData = response.data?.data || []
            this.storeVariantIndexData(responseData)
            const metadata = response.data?.metadata || {}
            const totalEntries = metadata.count ?? 0
            const size = metadata.size ?? perPage
            lastPage = Math.ceil(totalEntries / size)
            page += 1
          } while (page <= lastPage)
          if (arenaId === this.arenaId) {
            this.hasLoadedVariantProductIndex = true
          }
        } catch (error) {
          console.error('Failed to build arena variant index', error)
        } finally {
          this.variantIndexPromise = null
        }
      })()
      await this.variantIndexPromise
    },
    storeVariantIndexData(products = []) {
      products.forEach((product) => {
        const productId = product?.id
        const variants = Array.isArray(product?.variants)
          ? product.variants
          : []
        variants.forEach((variant) => {
          const variantId =
            variant?.id ||
            variant?.arena_variant_id ||
            variant?.variant_id ||
            ''
          if (!variantId) return
          if (!this.variantProductIndex[variantId]) {
            this.variantProductIndex[variantId] = {}
          }
          if (!this.variantProductIndex[variantId].productId) {
            this.variantProductIndex[variantId].productId =
              productId || variant?.arena_product_id || ''
          }
        })
      })
    },
    extractVariantGrossPrice(variantData) {
      const priceEntry = Array.isArray(variantData?.prices)
        ? variantData.prices[0]
        : null
      const grossPrice = Number(priceEntry?.gross_price)
      if (Number.isNaN(grossPrice)) return 0
      return this.roundCurrencyValue(grossPrice)
    },
    roundCurrencyValue(value) {
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return 0
      return Math.round(numeric * 100) / 100
    },
    applyPriceSummary(calculationId, standardPrice) {
      if (calculationId !== this.priceCalculationId) return
      const normalizedStandard = roundCurrencyValue(standardPrice)

      let discountAmount = 0
      let discountValue = 0
      let promoPrice = 0

      if (!this.currentPromotion) {
        this.priceSummary = {
          standardPrice: normalizedStandard,
          discountValue,
          promoPrice: normalizedStandard,
        }
        return
      }

      if (isFixedPricePromotion(this.currentPromotion)) {
        promoPrice = roundCurrencyValue(
          normalizeDiscountAmount(this.currentPromotion?.discount_value),
        )
        discountValue = roundCurrencyValue(normalizedStandard - promoPrice)
      } else {
        discountAmount = normalizeDiscountAmount(
          this.currentPromotion?.discount_value,
        )
        discountValue = roundCurrencyValue(
          normalizedStandard * (discountAmount / 100),
        )
        promoPrice = roundCurrencyValue(normalizedStandard - discountValue)
      }

      this.priceSummary = {
        standardPrice: normalizedStandard,
        discountValue,
        promoPrice,
      }
      if (this.isEditing) {
        this.localPromotion.standard_price = normalizedStandard
        this.localPromotion.promo_price = promoPrice
      }
    },

    handleSave() {
      if (this.isSaving) return
      if (!this.validate()) return
      // The parent listens to "save" and decides whether to call create or update APIs.
      this.$emit('save', { ...this.localPromotion })
      this.resetValidation()
    },
    handleEdit() {
      this.$emit('request-edit', this.promotion)
    },
    async handleImageChange(event) {
      if (!this.isEditing) return
      const [file] = event?.target?.files || []
      if (!file) {
        this.resetImageSelection()
        return
      }
      this.resetImageSelection()
      this.imageFile = file
      this.imagePreview = URL.createObjectURL(file)
      await this.uploadSelectedImage()
      if (event?.target) {
        event.target.value = ''
      }
    },
    async uploadSelectedImage() {
      if (!this.imageFile) return
      this.isUploadingImage = true
      try {
        const response = await FilesApi.upload('arenas', this.imageFile)
        if (!response.data?.link) {
          throw new Error('missing_image_link')
        }
        this.localPromotion.image = response.data.link
        this.resetImageSelection()
      } catch (error) {
        console.error(error)
        this.notifyImageUploadError()
        this.resetImageSelection()
      } finally {
        this.imageFile = null
        this.isUploadingImage = false
      }
    },
    notifyImageUploadError() {
      this.showSnackbar?.({
        color: 'error',
        message: this.$t('miscellaneous.fileUploadFailed'),
      })
    },
    notifyGroupDeletionError() {
      this.showSnackbar?.({
        color: 'error',
        message: this.$t('entities.promotions.card.deleteGroupError'),
      })
    },
    openDeleteDialog() {
      if (!this.canShowDeleteButton || this.isEditing) return
      this.deleteError = ''
      this.isDeleteDialogVisible = true
    },
    closeDeleteDialog() {
      if (this.isDeletingPromotion) return
      this.isDeleteDialogVisible = false
      this.deleteError = ''
    },
    async confirmDelete() {
      if (
        !this.canShowDeleteButton ||
        this.isEditing ||
        this.isDeletingPromotion
      ) {
        return
      }
      this.deleteError = ''
      this.isDeletingPromotion = true
      try {
        await this.deletePromotion()
        this.isDeleteDialogVisible = false
        this.$emit('delete', this.promotion)
      } catch (error) {
        console.error('Failed to delete promotion', error)
        this.deleteError = this.extractApiErrorMessage(error)
      } finally {
        this.isDeletingPromotion = false
      }
    },
    async deletePromotion() {
      const promotionId = this.promotion?.id
      if (!this.arenaId || !promotionId) {
        throw new Error('missing_promotion_context')
      }
      await PromotionsApi.delete(this.arenaId, promotionId)
    },
    extractApiErrorMessage(error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message
      return apiMessage || this.$t('errors.default')
    },
    openGroupDialog(group = {}) {
      this.selectedGroupForUpsert = { ...group }
      if (!this.isEditing) return
      this.isGroupDialogVisible = true
    },
    async handleGroupSave(payload) {
      const previousPersistedVariantIds = normalizeVariantIdList(
        this.selectedGroupForUpsert?.variants || [],
      )
      const previousPendingVariantIds = normalizeVariantIdList(
        this.selectedGroupForUpsert?.variantIds || [],
      )
      const normalizedVariantIds = normalizeVariantIdList(
        payload.variantIds || [],
      )
      const isPersistedTarget = this.isPersistedGroup(
        this.selectedGroupForUpsert,
      )
      const pendingVariantIds = this.prepareVariantIdsForStorage(
        normalizedVariantIds,
        previousPersistedVariantIds,
        previousPendingVariantIds,
        isPersistedTarget,
      )
      const variants = Array.isArray(payload.variants)
        ? payload.variants.map((variant) => ({ ...variant }))
        : []
      const existingId =
        payload.id ||
        this.selectedGroupForUpsert.id ||
        this.selectedGroupForUpsert.tempId ||
        ''
      const groupId = existingId || this.generateTempGroupId()
      const categoryId =
        payload.category_id ||
        this.selectedGroupForUpsert.category_id ||
        getCategoryIdByCode(this.categories, payload.category_code) ||
        ''
      const categoryCode =
        payload.category_code ||
        this.selectedGroupForUpsert.category_code ||
        getCategoryCodeById(this.categories, categoryId) ||
        extractCategoryCodeFromVariants(variants)
      const nextGroup = {
        id: groupId,
        tempId:
          this.selectedGroupForUpsert.tempId || (payload.id ? '' : groupId),
        name: payload.name,
        quantity: payload.quantity,
        variantIds: pendingVariantIds,
        variants,
        category_id: categoryId,
        category_code: categoryCode,
      }
      const groups = Array.isArray(this.localPromotion.groups)
        ? [...this.localPromotion.groups]
        : []
      const existingIndex = groups.findIndex(
        (group) =>
          group.id === groupId ||
          (nextGroup.tempId && group.tempId === nextGroup.tempId),
      )
      if (existingIndex >= 0) {
        groups.splice(existingIndex, 1, {
          ...groups[existingIndex],
          ...nextGroup,
        })
      } else {
        groups.push(nextGroup)
      }
      this.localPromotion.groups = groups
      this.isGroupDialogVisible = false
      await this.persistGroupVariants(
        nextGroup,
        previousPersistedVariantIds,
        normalizedVariantIds,
      )
      this.selectedGroupForUpsert = {}
    },
    isGroupDeleting(group) {
      const key = this.getGroupKey(group)
      if (!key) return false
      return Boolean(this.groupDeletionStates[key])
    },
    setGroupDeletingState(key, isDeleting) {
      if (!key) return
      if (isDeleting) {
        this.$set(this.groupDeletionStates, key, true)
      } else {
        this.$delete(this.groupDeletionStates, key)
      }
    },
    removeGroupByKey(key) {
      if (!key) return
      this.localPromotion.groups = Array.isArray(this.localPromotion.groups)
        ? this.localPromotion.groups.filter(
            (group) => this.getGroupKey(group) !== key,
          )
        : []
    },
    async handleGroupDelete(group) {
      if (!this.isEditing || !group) return
      const groupKey = this.getGroupKey(group)
      if (!groupKey || this.isGroupDeleting(group)) return
      const promotionId = this.getPromotionPersistenceId()
      const canDeletePersisted =
        this.isPersistedGroup(group) && this.arenaId && promotionId
      if (!canDeletePersisted) {
        this.removeGroupByKey(groupKey)
        return
      }
      this.setGroupDeletingState(groupKey, true)
      try {
        await PromotionsApi.deletePromotionGroup(
          this.arenaId,
          promotionId,
          group.id,
        )
        this.removeGroupByKey(groupKey)
      } catch (error) {
        console.error('Failed to delete promotion group', error)
        this.notifyGroupDeletionError()
      } finally {
        this.setGroupDeletingState(groupKey, false)
      }
    },
    canPersistGroupVariants(group) {
      return !(
        !group ||
        !this.arenaId ||
        !this.getPromotionPersistenceId() ||
        !this.isPersistedGroup(group)
      )
    },
    isPersistedGroup(group) {
      if (!group || !group.id) return false
      return !(group.tempId && group.tempId === group.id)
    },
    getPromotionPersistenceId() {
      return this.localPromotion?.id || this.promotion?.id || ''
    },
    async persistGroupVariants(
      group,
      previousVariantIds = [],
      nextVariantIds = [],
    ) {
      if (!this.canPersistGroupVariants(group)) return
      const promotionId = this.getPromotionPersistenceId()
      const existingIds = new Set(
        normalizeVariantIdList(previousVariantIds || []),
      )
      const currentIds = normalizeVariantIdList(group.variantIds || [])
      const targetIds = new Set(normalizeVariantIdList(nextVariantIds || []))
      const pendingVariantIds = currentIds.filter(
        (variantId) => !existingIds.has(variantId),
      )
      const removedVariantIds = Array.from(existingIds).filter(
        (variantId) => !targetIds.has(variantId),
      )
      if (!pendingVariantIds.length && !removedVariantIds.length) return
      if (pendingVariantIds.length) {
        try {
          const persistedVariantIds = await this.createPromotionGroupVariants(
            promotionId,
            group.id,
            pendingVariantIds,
          )
          if (persistedVariantIds.length) {
            this.removePersistedVariantIds(group.id, persistedVariantIds)
          }
        } catch (error) {
          console.error('Failed to persist promotion group variants', error)
        }
      }
      if (removedVariantIds.length) {
        try {
          await this.deletePromotionGroupVariants(
            promotionId,
            group.id,
            removedVariantIds,
          )
        } catch (error) {
          console.error('Failed to remove promotion group variants', error)
        }
      }
    },
    async createPromotionGroupVariants(promotionId, groupId, variantIds = []) {
      const persistedVariantIds = []
      for (const variantId of variantIds) {
        try {
          await PromotionsApi.createPromotionGroupVariant(
            this.arenaId,
            promotionId,
            groupId,
            { arena_variant_id: variantId },
          )
          persistedVariantIds.push(variantId)
        } catch (error) {
          console.error(
            `Failed to create promotion group variant for ${variantId}`,
            error,
          )
        }
      }
      return persistedVariantIds
    },
    async deletePromotionGroupVariants(promotionId, groupId, variantIds = []) {
      for (const variantId of variantIds) {
        try {
          await PromotionsApi.deletePromotionGroupVariant(
            this.arenaId,
            promotionId,
            groupId,
            variantId,
          )
        } catch (error) {
          console.error(
            `Failed to delete promotion group variant ${variantId}`,
            error,
          )
        }
      }
    },
    removePersistedVariantIds(groupId, persistedVariantIds = []) {
      if (!persistedVariantIds.length) return
      const persistedSet = new Set(
        persistedVariantIds
          .map((variantId) => normalizeVariantId(variantId))
          .filter(Boolean),
      )
      if (!Array.isArray(this.localPromotion.groups)) return
      this.localPromotion.groups = this.localPromotion.groups.map((group) => {
        if (group.id !== groupId) return group
        const remainingVariantIds = Array.isArray(group.variantIds)
          ? group.variantIds.filter(
              (variantId) => !persistedSet.has(normalizeVariantId(variantId)),
            )
          : []
        return {
          ...group,
          variantIds: remainingVariantIds,
        }
      })
    },
    handleCancel() {
      this.localPromotion = this.createLocalPromotion(this.promotion)
      this.resetValidation()
      this.resetImageSelection()
      this.resetGroupDeletionStates()
      this.$emit('cancel', this.promotion)
    },
    validate() {
      const errors = {}
      if (!this.localPromotion.name?.trim()) {
        errors.name = this.$t('validation.required') || 'Name is required.'
      }

      const formattedDiscount = this.applyDiscountFormatting()
      const hasDiscountValue = String(formattedDiscount ?? '').trim()

      if (!hasDiscountValue) {
        errors.discount_value = this.$t('validation.required')
      } else if (
        !this.isValidDiscountString(this.localPromotion.discount_value)
      ) {
        errors.discount_value = this.$t(
          'entities.promotions.validation.invalidDiscountAmount',
        )
      }
      this.errors = errors
      return Object.keys(errors).length === 0
    },
    isValidDiscountString(value) {
      if (value === null || value === undefined) return false
      const normalized = String(value).trim()
      if (!/^\d+\.\d+$/.test(normalized)) {
        return false
      }

      const numberValue = Number(normalized)
      if (isFixedPricePromotion(this.localPromotion)) {
        const standardPriceNum = Number(this.localPromotion.standard_price)
        return !(
          Number.isNaN(numberValue) ||
          numberValue <= 0 ||
          (standardPriceNum > 0 && numberValue > standardPriceNum)
        )
      }

      if (Number.isNaN(numberValue) || numberValue <= 0 || numberValue >= 100) {
        return false
      }

      const [, fractional = ''] = normalized.split('.')
      const integerPart = Math.floor(numberValue)
      return !(integerPart < 10 && fractional.length < 2)
    },
    resetValidation() {
      this.errors = {}
    },
    resetImageSelection() {
      if (this.imagePreview) {
        URL.revokeObjectURL(this.imagePreview)
      }
      this.imagePreview = ''
      this.imageFile = null
    },
    resetGroupDeletionStates() {
      this.groupDeletionStates = {}
    },
    resetDeleteState() {
      this.isDeleteDialogVisible = false
      this.deleteError = ''
      this.isDeletingPromotion = false
    },
    resetVariantPricingCache() {
      this.variantDetailsCache = {}
      this.variantProductIndex = {}
      this.hasLoadedVariantProductIndex = false
      this.priceSummary = createPriceSummaryState()
    },
    updatePromotionName(value) {
      this.$set(this.localPromotion, 'name', value)
    },
    updatePromotionDescription(value) {
      this.$set(this.localPromotion, 'description', value)
    },
    updatePromotionActive(value) {
      this.$set(this.localPromotion, 'active', Boolean(value))
    },
  },
}
</script>

<style scoped>
.promotion-card {
  border-radius: 16px;
  padding: 1.5rem;
  background-color: #fff;
  transition: box-shadow 0.2s ease;
}

.promotion-card__header {
  border-bottom: 1px solid #e4e8f0;
  padding-bottom: 0.5rem;
}

.promotion-card__image-wrapper {
  border: 2px dashed #b5bfd6;
  border-radius: 12px;
  min-height: 10.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #fafbff;
}

.promotion-card__image-row {
  width: 100%;
  flex-direction: column;
}

.promotion-card__image-row .promotion-card__image-wrapper {
  flex: 1;
}

.promotion-card__image-placeholder {
  color: #6f778a;
  text-align: center;
  padding: 1rem;
  font-weight: 500;
}

.promotion-card__components-header {
  border-bottom: 1px solid #e4e8f0;
  padding-bottom: 0.5rem;
}

.promotion-card__icon-actions .v-btn {
  background-color: #f4f6fb;
}

.promotion-card__errors {
  max-width: 30rem;
}

.promotion-card__image-wrapper {
  position: relative;
  overflow: hidden;
}

.promotion-card__image-wrapper--editable {
  cursor: pointer;
}

.promotion-card__image-wrapper--has-image {
  border: none;
  background-color: transparent;
}

.promotion-card__image-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}

.promotion-card__image-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  color: #fff;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 1;
  text-transform: uppercase;
  font-size: 0.85rem;
}

.promotion-card__image-wrapper--editable:hover .promotion-card__image-overlay,
.promotion-card__image-wrapper--editable:focus-within
  .promotion-card__image-overlay,
.promotion-card__image-wrapper--editable:active .promotion-card__image-overlay,
.promotion-card__image-wrapper--editable:focus .promotion-card__image-overlay {
  opacity: 1;
}
</style>
